import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import COURSES from './satCourses.json';
import Tile from './Tile';
import FeatureHeader from './FeatureHeader';
import { SLOW, useReveal } from './useReveal';
import BuffyPeek from './BuffyPeek';

/* F1 "Any curriculum, connected to the SAT." (PAGE STRUCTURE v3, 2026-09-29:
   the first feature, text on the left; was the S2 "Any curriculum" section).
   The three courses are EXAMPLES of what Buffy does with the curriculum a
   teacher drops in. The map's mode follows ITS OWN width (the visual
   column), not the viewport: a map from 860px (compact under 1100), a list
   below. F1 stacks under 1264px so the map keeps the full container.

   Owner 2026-09-29 (round 2): no course buttons. The map plays the three
   courses on a slow loop, Algebra 1 -> Geometry -> Algebra 2 -> ...: the
   rows rise, the chips pop, the connectors draw, a readable hold, then the
   windows' content fades out, the course swaps (the tile height eases to the
   new course) and the next one plays in. About 7.6s per course at the 1.35x
   pace. The loop pauses off screen and while the pointer is on the map; reduced motion and ?settled=1 show Algebra 1, settled. The course
   name in the window head says which course is showing.

   Data: satCourses.json, extracted from the curriculum fixture (courses
   algebra-1, geometry, algebra-2; DIRECT links only; skill names from the
   fixture's `concepts`). Row = [n, title, skillName, domain, moreDirectLinks];
   a 2-item row has no direct link. No publisher names. Counts on screen are
   computed from the rows: 10 of 11, 8 of 13, 9 of 12.

   No skill name repeats inside a course: when a later chapter's only direct
   link is a skill an earlier chapter already shows (Geometry ch 5 and 8 ->
   ch 3), it gets no chip of its own and its connector runs to that chip.

   Look (owner 2026-09-29, round 2): a soft blurred wallpaper tile (Tile
   WALLS.violet) with the two white windows (.cx-surface) inset 24px (16 on
   phones). Map: the Course window, the SAT skills window, connectors
   measured from the real DOM (offsetLeft/offsetTop, so entrance transforms
   never skew them) and re-measured by a ResizeObserver. List (under 860):
   one window, the first 6 chapters and a "+ N more chapters" row, each
   skill as a dot and plain text under its chapter, no connectors.

   No page jump (final design review #1): the windows are pinned to the
   TALLEST course at the current width, so the tile never changes height
   during the loop. A hidden measurer lays out every course's full chapter
   list in the live mode and width. Map: the pin is the longest course (13
   rows); a shorter one leaves white space at the bottom of its windows.
   List (phones): the pin is the tallest "first 6 chapters + more" window,
   and each course then shows as many chapters as fit in it (at least 6),
   so the window is filled instead of half empty (Geometry's short gray
   rows let it show more).

   Connectors are straight, row to row (owner linking rule). A chapter whose
   only skill an earlier chapter already shows routes to THAT chip on a
   smooth curve that leaves its row flat and lands flat on the chip, so the
   lines converge (owner 2026-09-30: "I liked when multiple chapters linked
   to a skill"; this undoes the qa-final-design #6 "Same skill as chapter N"
   text in the map). The phone list, which has no lines, keeps the text. */

const DOT = { algebra: '#2563EB', advanced: '#3B82F6', psda: '#60A5FA', geotrig: '#1E40AF' };
const PHONE_ROWS = 6;
const NBSP = String.fromCharCode(160);
/* loop timing in unscaled ms (x SLOW at run time). HOLD runs from the swap
   (or the first reveal) to the fade-out and includes the entrance: first
   course 5600 (7.6s: a 2.4s entrance, then 5.2s to read), later courses
   5200 (7.0s: a 1.7s entrance, then 5.3s); the fade-out is 420 (0.57s). */
const HOLD_FIRST = 5600;
const HOLD_NEXT = 5200;
const FADE = 420;

function offsetIn(el, root) {
  let x = 0;
  let y = 0;
  let n = el;
  while (n && n !== root) {
    x += n.offsetLeft;
    y += n.offsetTop;
    n = n.offsetParent;
  }
  return n === root ? { x, y } : null;
}

// rows -> chapters, with `target` = the row whose chip this chapter uses
function toChapters(course) {
  const first = {};
  return course.chapters.map(([n, title, skill, domain, more], i) => {
    const ch = { n, title, skill, domain, more: more || 0, target: null, sameAs: null };
    if (skill) {
      if (first[skill] == null) {
        first[skill] = i;
        ch.target = i;
      } else {
        ch.target = first[skill];
        ch.sameAs = course.chapters[first[skill]][0];
      }
    }
    return ch;
  });
}

/* the Course window's rows: the first `limit` chapters, then "+ N more"
   when some are left; `more` forces the "+ N more" row (the measurer);
   `rowRefs` only for the live window */
function Rows({ chapters, limit, more, rowRefs }) {
  const left = chapters.length - limit;
  return (
    <>
      {chapters.map((ch, i) => (i >= limit ? null : (
        <li
          key={ch.n}
          ref={rowRefs ? (el) => { rowRefs.current[i] = el; } : undefined}
          className={`cxs-crow${ch.skill ? '' : ' is-off'}`}
        >
          <span className="cxs-crow-main">
            <span className="cxs-crow-n" aria-hidden="true">{ch.n}</span>
            <span className="cxs-crow-t">
              <span className="cxs-sr">Chapter {ch.n}: </span>
              {ch.title}
            </span>
          </span>
          {ch.skill && !ch.sameAs ? (
            <span className="cxs-crow-chip" aria-hidden="true">
              <span className="cx-chip cxs-chip cxs-chip-line">
                <span className="cx-chip-dot" style={{ background: DOT[ch.domain] }} />
                {ch.skill}
              </span>
              {ch.more ? <span className="cxs-more">+{ch.more} more</span> : null}
            </span>
          ) : null}
          {ch.sameAs ? (
            <span className="cxs-crow-chip cxs-crow-same" aria-hidden="true">Same skill as chapter {ch.sameAs}</span>
          ) : null}
          <span className="cxs-sr">
            {ch.skill ? `. SAT skill: ${ch.skill}${ch.more ? `, and ${ch.more} more` : ''}.` : '. No direct SAT skill.'}
          </span>
        </li>
      )))}
      {/* list mode only; the map shows every chapter, so its last row is
          the real :last-child (qa #23) */}
      {left > 0 || more ? (
        <li className="cxs-crow cxs-crow-more" aria-hidden="true">
          + {Math.max(left, 1)} more chapters
        </li>
      ) : null}
    </>
  );
}

const ALL = COURSES.map((c) => ({ id: c.id, name: c.name, chapters: toChapters(c) }));

/* The scene alone (schools page 2026-10-01: its feature tabs show it
   without this section's heading). */
export function CourseScene() {
  const { ref, reveal, live, near, still } = useReveal({ amount: 0.3 });
  const [idx, setIdx] = useState(0);
  // `out`: the windows' content is fading out before the swap
  const [out, setOut] = useState(false);
  // after the first course, entrances use the shorter loop timing
  const [cycled, setCycled] = useState(false);
  // the pointer on the map pauses the loop (there is nothing to focus in it)
  const [held, setHeld] = useState(false);
  const course = COURSES[idx] || COURSES[0];
  const courseId = course.id;
  const chapters = toChapters(course);
  const linked = chapters.filter((c) => c.skill).length;

  // the visual column's width decides the mode (map / compact map / list)
  const visRef = useRef(null);
  const [visW, setVisW] = useState(1200);
  useLayoutEffect(() => {
    const el = visRef.current;
    if (!el) return undefined;
    const set = () => setVisW(el.clientWidth);
    set();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(set);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  // the compact map needs 840: 24 + the course window 376 (its longest
  // title) + 32 + the skills window 384 (its longest chip, 352 + 20 + 12) + 24.
  // F1's 300px copy column leaves it 844 beside the text (final formatting #9)
  const wide = visW >= 840;
  const compact = wide && visW < 1100;

  // the loop: hold -> fade out -> swap -> hold ... (paused off screen /
  // held / the pause button). A hold or pause during the fade-out brings
  // the course back instead of freezing on empty windows.
  useEffect(() => {
    if (still || reveal !== 'play' || !live || held) {
      if (out && held) setOut(false);
      return undefined;
    }
    if (out) {
      const t = setTimeout(() => {
        setIdx((i) => (i + 1) % COURSES.length);
        setCycled(true);
        setOut(false);
      }, FADE * SLOW);
      return () => clearTimeout(t);
    }
    const t = setTimeout(() => setOut(true), (cycled ? HOLD_NEXT : HOLD_FIRST) * SLOW);
    return () => clearTimeout(t);
  }, [still, reveal, live, held, out, idx, cycled]);

  const tileRef = useRef(null);
  const clipRef = useRef(null);
  const gridRef = useRef(null);
  const winRef = useRef(null);
  const skillsRef = useRef(null);
  const rowRefs = useRef([]);
  const chipRefs = useRef([]);
  rowRefs.current.length = chapters.length;
  chipRefs.current.length = chapters.length;
  const [links, setLinks] = useState([]);
  // the connectors are white over the wallpaper and blue inside the SAT
  // skills window (a white line would vanish on the window's white): one
  // shared gradient, hard stop at the window's left edge
  const [grad, setGrad] = useState(null);
  const [tileH, setTileH] = useState(null);
  // the tallest course's window height at this width (the measurer)
  const [winMin, setWinMin] = useState(null);
  // map mode: the current course's rows share the pinned window height
  // (final formatting S3 #21: no empty band under a shorter course)
  const [crowH, setCrowH] = useState(null);
  // list mode: how many chapters each course shows (fills the pinned window)
  const [fit, setFit] = useState({});
  const measRef = useRef(null);

  const measure = useCallback(() => {
    const tile = tileRef.current;
    const clip = clipRef.current;
    const win = winRef.current;
    const grid = gridRef.current;
    if (!tile || !clip || !win || !grid) return;
    // border-box height of the tile = the windows + the clip's insets (24,
    // 16 on phones; read from CSS) + any border
    const cs = getComputedStyle(clip);
    const border = tile.offsetHeight - tile.clientHeight;
    const wins = measRef.current ? [...measRef.current.querySelectorAll('.cxs-course-mwin')] : [];
    let tallest = null;
    if (wins.length) {
      // per course: the window's fixed part (head, list padding, hairline),
      // each chapter row's height, the "+ N more" row's height
      const parts = wins.map((m) => {
        const rows = [...m.querySelectorAll('.cxs-crow:not(.cxs-crow-more)')].map((r) => r.getBoundingClientRect().height);
        const moreEl = m.querySelector('.cxs-crow-more');
        const moreH = moreEl ? moreEl.getBoundingClientRect().height : 0;
        const fixed = m.getBoundingClientRect().height - rows.reduce((a, b) => a + b, 0) - moreH;
        return { rows, moreH, fixed };
      });
      const heightAt = (c, k) => c.fixed + c.rows.slice(0, k).reduce((a, b) => a + b, 0) + (k < c.rows.length ? c.moreH : 0);
      if (wide) {
        tallest = Math.ceil(Math.max(...parts.map((c) => heightAt(c, c.rows.length))));
        const cur = parts[Math.max(0, ALL.findIndex((c) => c.id === courseId))];
        const base = cur.rows.length ? Math.max(...cur.rows) : 34;
        setCrowH(cur.rows.length ? Math.max(base, Math.floor((tallest - cur.fixed) / cur.rows.length)) : null);
      } else {
        setCrowH(null);
        tallest = Math.ceil(Math.max(...parts.map((c) => heightAt(c, Math.min(PHONE_ROWS, c.rows.length)))));
        const next = {};
        parts.forEach((c, i) => {
          let k = Math.min(PHONE_ROWS, c.rows.length);
          while (k < c.rows.length && heightAt(c, k + 1) <= tallest + 0.5) k += 1;
          next[ALL[i].id] = k;
        });
        setFit((f) => (JSON.stringify(f) === JSON.stringify(next) ? f : next));
      }
    }
    setWinMin(tallest);
    setTileH((tallest || grid.offsetHeight) + parseFloat(cs.paddingTop) + parseFloat(cs.paddingBottom) + border);
    if (!wide) {
      setLinks([]);
      return;
    }
    const w = offsetIn(win, tile);
    const sk = skillsRef.current && offsetIn(skillsRef.current, tile);
    if (!w || !sk) return;
    const sx = w.x + win.offsetWidth;
    const res = [];
    let k = 0;
    const own = [];
    const shared = [];
    chapters.forEach((ch, i) => {
      if (!ch.skill) return;
      const row = rowRefs.current[i];
      const chip = chipRefs.current[ch.target];
      if (!row || !chip) return;
      const r = offsetIn(row, tile);
      const c = offsetIn(chip, tile);
      if (!r || !c) return;
      const sy = Math.round(r.y + row.offsetHeight / 2) + 0.5;
      const ex = c.x;
      const ey = Math.round(c.y + chip.offsetHeight / 2) + 0.5;
      if (ch.target === i) {
        own.push({ id: `${courseId}-${i}`, sx, sy, ex, ey, d: `M${sx} ${sy} L${ex} ${ey}` });
      } else {
        // flat out of the row, flat into the shared chip
        const mx = (sx + ex) / 2;
        shared.push({ id: `${courseId}-${i}`, sx, sy, ex, ey, d: `M${sx} ${sy} C${mx} ${sy} ${mx} ${ey} ${ex} ${ey}` });
      }
    });
    // the chips' own lines draw first, then the converging ones
    own.concat(shared).forEach((l) => { res.push({ ...l, k }); k += 1; });
    setLinks(res);
    const ex = res.length ? res[0].ex : sk.x;
    setGrad(ex > sx ? { x1: sx, x2: ex, o: Math.min(1, Math.max(0, (sk.x - sx) / (ex - sx))) } : null);
    // chapters is derived from courseId on every render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId, wide, compact, crowH]);

  useLayoutEffect(() => { measure(); }, [measure]);

  useEffect(() => {
    const grid = gridRef.current;
    if (!grid) return undefined;
    let ro = null;
    if (typeof ResizeObserver !== 'undefined') {
      // the grid, not the tile: the tile's own height eases on a swap
      ro = new ResizeObserver(() => measure());
      ro.observe(grid);
    }
    let alive = true;
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => { if (alive) measure(); });
    return () => {
      alive = false;
      if (ro) ro.disconnect();
    };
  }, [measure]);

  // entrance delays (unscaled ms): the first reveal is slower than a swap
  const rowsD = cycled ? 0 : 160;
  const chipD = (k) => (cycled ? 160 + k * 60 : 420 + k * 80);
  const linkD = (k) => (cycled ? 220 + k * 60 : 520 + k * 80);
  let chipOrder = 0;
  const chipDelay = [];
  chapters.forEach((ch, i) => {
    if (ch.skill && ch.target === i) {
      chipDelay[i] = chipD(chipOrder);
      chipOrder += 1;
    }
  });

  const hold = (on) => () => setHeld(on);

  return (
    <div ref={visRef} className="cxs-course-visual">
      <div ref={ref} className="cxs-course-stage" data-reveal={reveal} data-live={live ? 'true' : 'false'}>
        <Tile
          ref={tileRef}
          wall="violet"
          near={near}
          className={`cxs-course-tile ${wide ? 'is-map' : 'is-list'}${compact ? ' is-compact' : ''}${out ? ' is-out' : ''}`}
          style={tileH ? { height: tileH } : undefined}
          onPointerEnter={hold(true)}
          onPointerLeave={hold(false)}
        >
          {/* Buffy peeks over the top edge, behind the wallpaper (BuffyPeek.jsx) */}
          <BuffyPeek edge="top" reveal={reveal} paused={!live} />
          <div ref={clipRef} className="cxs-course-clip">
            <div ref={gridRef} className="cxs-course-grid" style={wide && crowH ? { '--crow-h': `${crowH}px` } : undefined}>
              <div ref={winRef} className="cx-surface cxs-course-win cxs-in-panel" style={winMin ? { minHeight: winMin } : undefined}>
                <div className="cxs-cfade">
                  <div key={`head-${courseId}`} className="cxs-course-head cxs-in-fade" style={{ '--d': cycled ? '0ms' : '120ms' }}>
                    <span className="cx-t-head">{course.name}</span>
                    <span className="cx-t-label cxs-muted">{chapters.length} chapters</span>
                  </div>
                  {/* The list enters as ONE element (and again on each
                      swap): Chromebook budget of about 12 animating parts. */}
                  <ol key={courseId} className="cxs-course-rows cxs-in-rise" style={{ '--d': `${rowsD}ms` }}>
                    <Rows chapters={chapters} limit={wide ? chapters.length : (fit[courseId] || PHONE_ROWS)} rowRefs={rowRefs} />
                  </ol>
                </div>
              </div>

              <div ref={skillsRef} className="cx-surface cxs-course-skills cxs-in-panel" style={{ '--d': '90ms', minHeight: winMin || undefined }} aria-hidden="true">
                <div className="cxs-cfade">
                  <div key={`head-${courseId}`} className="cxs-course-head cxs-in-fade" style={{ '--d': cycled ? '0ms' : '210ms' }}>
                    <span className="cx-t-head">SAT skills</span>
                    <span className="cx-t-label cxs-muted">in {linked} of {chapters.length} chapters</span>
                  </div>
                  <ol key={courseId} className="cxs-course-rows">
                    {chapters.map((ch, i) => (
                      <li key={ch.n} className="cxs-slot">
                        {ch.skill && ch.target === i ? (
                          <span className="cxs-slot-pop cxs-in-pop" style={{ '--d': `${chipDelay[i]}ms` }}>
                            <span ref={(el) => { chipRefs.current[i] = el; }} className="cx-chip cxs-chip cxs-chip-line">
                              <span className="cx-chip-dot" style={{ background: DOT[ch.domain] }} />
                              {ch.skill}
                            </span>
                            {ch.more ? <span className="cxs-more">+{ch.more} more</span> : null}
                          </span>
                        ) : null}
                      </li>
                    ))}
                  </ol>
                </div>
              </div>
            </div>

            {/* One element per link draws (the line); the dot fades in with it. */}
            <svg className="cxs-links" aria-hidden="true" focusable="false">
              {grad ? (
                <defs>
                  <linearGradient id="cxs-link-grad" gradientUnits="userSpaceOnUse" x1={grad.x1} y1="0" x2={grad.x2} y2="0">
                    <stop className="cxs-stop-wall" offset="0" />
                    <stop className="cxs-stop-wall" offset={grad.o} />
                    <stop className="cxs-stop-win" offset={grad.o} />
                    <stop className="cxs-stop-win" offset="1" />
                  </linearGradient>
                </defs>
              ) : null}
              {links.map((l) => (
                <g key={l.id} className="cxs-link" style={{ '--d': `${linkD(l.k)}ms` }}>
                  <path className="cxs-link-line" d={l.d} pathLength="1" />
                  <circle className="cxs-link-dot" cx={l.sx} cy={l.sy} r="3.5" />
                </g>
              ))}
            </svg>
          </div>
        </Tile>
        {/* the measurer: every course's Course window laid out in this
            mode and width; the tallest pins both windows */}
        <div ref={measRef} className={`cxs-course-meas cxs-course-tile ${wide ? 'is-map' : 'is-list'}${compact ? ' is-compact' : ''}`} aria-hidden="true">
          {ALL.map((c) => (
            <div key={c.id} className="cxs-course-grid">
              <div className="cx-surface cxs-course-mwin">
                <div className="cxs-course-head">
                  <span className="cx-t-head">{c.name}</span>
                  <span className="cx-t-label cxs-muted">{c.chapters.length} chapters</span>
                </div>
                <ol className="cxs-course-rows">
                  <Rows chapters={c.chapters} limit={c.chapters.length} more={!wide} />
                </ol>
              </div>
              <div />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* `header` (schools preview, 2026-09-30): optional FeatureHeader overrides
   (title, sub, side, id, as, labelled, after, className added to ours) so the
   schools page can reuse this scene under its own words. With no props the
   teacher page renders exactly as before. */
export default function CourseSection({ header = {} }) {
  return (
    <FeatureHeader
      title={(
        <>
          <span>Any curriculum,</span>
          {' '}
          {/* no-break spaces: it breaks only as "connected to / the SAT." */}
          <span className="cxs-h2-tail">{`connected${NBSP}to the${NBSP}SAT.`}</span>
        </>
      )}
      sub="Pick the curriculum you already teach from our list. Buffy finds where the SAT shows up in it."
      side="left"
      id="cxs-f1"
      {...header}
      className={`cxs-course cxs-feature-map${header.className ? ` ${header.className}` : ''}`}
    >
      <CourseScene />
    </FeatureHeader>
  );
}
